import { useState } from "react";
import { ICustomerType } from "@vmosolutions/oc-interfaces";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { FormikTextField } from "../../../MUI5FormikComponents/FormikWrappers";
import { Button, Grid, Typography, Paper, Modal, Box, ButtonGroup } from "@mui/material";
import * as Yup from "yup";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
interface ICustomerTypeFormProps {
  customerTypeOptions: ICustomerType[];
  updateCustomerTypeList: (cusType: ICustomerType, mode: "create" | "update" | "delete") => void;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  showCustomerTypeModal: boolean;
  setShowCustomerTypeModal: any;
}
const initialCustomerTypeValues: ICustomerType = {
  title: "",
  description: "",
};

type CustomerTypeFormType = FormikProps<ICustomerType>;
const CustomerTypeSchema = Yup.object().shape({
  title: Yup.string().required("Title is required").nullable(),
  description: Yup.string().required("Description is required").nullable(),
});

export const CustomerTypeForm = (props: ICustomerTypeFormProps): JSX.Element => {
  const {
    customerTypeOptions,
    updateCustomerTypeList,
    setActiveModal,
    showCustomerTypeModal,
    setShowCustomerTypeModal,
  } = props;
  const dispatch = useDispatch();
  const [selectedType, setSelectedType] = useState<ICustomerType>();
  const initialValues = selectedType ? selectedType : initialCustomerTypeValues;

  const handleSubmit = async (values: ICustomerType, helpers: FormikHelpers<ICustomerType>): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    const mode = values._id ? "update" : "create";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Customer Type...`,
      successMessage: `Customer Type Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Customer Type`,
    };
    const response =
      mode === "create"
        ? await AmplifyAPIWrapper.Post<ICustomerType>("/customertype", values, notifications)
        : await AmplifyAPIWrapper.Put<ICustomerType>(`/customertype/${values._id}`, values, notifications);
    if (response?._id) {
      resetForm({ values });
      setStatus({ success: true });
      updateCustomerTypeList(response, mode);
      setSelectedType(response);
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  const handleDelete = (deleteType: ICustomerType): void => {
    if (!deleteType._id) {
      return null;
    }
    setActiveModal({
      title: "Delete Customer Type",
      message: "Please confirm that you want to delete this customer type",
      cancel: () => setActiveModal(null),
      confirm: async (): Promise<void> => {
        setActiveModal(null);
        const notifications: INotifications = {
          dispatch: dispatch,
          infoMessage: "Deleting Customer Type...",
          successMessage: "Customer Type Successfully Deleted.",
          errorMessage: "Error: Unable to delete Customer Type",
        };
        const response: any = await AmplifyAPIWrapper.Del(`/customertype/${deleteType._id}`, null, notifications);
        if (!response?.error) {
          updateCustomerTypeList(deleteType, "delete");
          setSelectedType(initialCustomerTypeValues);
        }
      },
      confirmLabel: "Confirm",
    });

    return null;
  };

  return (
    <Modal open={showCustomerTypeModal}>
      <Paper elevation={4} sx={{ width: "65%", minWidth: 350, margin: "auto", marginTop: "10%", p: 4 }}>
        <Grid container>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={6}>
              {customerTypeOptions.map((customerType: ICustomerType, key: number) => {
                return (
                  <DraggableListItem
                    key={key}
                    active={customerType._id === selectedType?._id ? true : false}
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      cursor: "pointer",
                      justifyContent: "space-between",
                    }}
                    onClick={() => setSelectedType(customerType)}
                  >
                    <Box>{customerType.title}</Box>
                    <Box>
                      <EditIcon
                        fontSize="small"
                        sx={{ cursor: "pointer", mr: 1 }}
                        onClick={() => setSelectedType(customerType)}
                      />
                      <DeleteIcon
                        fontSize="small"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleDelete(customerType)}
                      />
                    </Box>
                  </DraggableListItem>
                );
              })}
              <Button
                onClick={() => setSelectedType(initialCustomerTypeValues)}
                variant="contained"
                color="success"
                size="small"
                fullWidth
              >
                <AddIcon fontSize="small" /> Add New Customer Type
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validationSchema={CustomerTypeSchema}
              >
                {(customerTypeForm: CustomerTypeFormType) => {
                  const { dirty, isValid, isSubmitting, handleReset } = customerTypeForm;
                  return (
                    <Form>
                      <Grid container>
                        <Typography className="required_fields_label">required fields</Typography>
                        <Grid item xs={12}>
                          <FormikTextField label="Title" name="title" />
                          <FormikTextField
                            label="Description"
                            name="description"
                            multiline={true}
                            minRows={3}
                            maxRows={10}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ justifyContent: "end", display: "flex", pb: 2 }}>
                          <ButtonGroup variant="contained" size="small">
                            <Button
                              type="reset"
                              color="warning"
                              disabled={(!dirty && isValid) || isSubmitting}
                              onClick={handleReset}
                            >
                              Reset
                            </Button>
                            <Button color="success" disabled={isSubmitting || !isValid || !dirty} type="submit">
                              {selectedType ? "Update" : "Create"}
                            </Button>
                          </ButtonGroup>
                        </Grid>
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "end", pt: 2, borderTop: "1px solid grey" }}>
            <Button
              size="small"
              onClick={() => {
                setSelectedType(initialCustomerTypeValues);
                setShowCustomerTypeModal(false);
              }}
              variant="contained"
              color="error"
            >
              Close
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
};
